import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Calendar, 
  Image, 
  ArrowRight, 
  Check, 
  Clock, 
  Users, 
  Zap,
  TrendingUp,
  Target,
  Shield,
  Sparkles,
  Crown
} from 'lucide-react';
import { Button } from './ui/button';

const SolucoesSection = () => {
  const navigate = useNavigate();

  // Product mockup images
  const mockups = {
    orcamento: "https://static.prod-images.emergentagent.com/jobs/c7d28edb-cb45-4bc7-8f27-ae38510fc8f7/images/feee49320d21566850b799563505abfd7da1f6037fdaef355fcff827370c758a.png",
    agenda: "https://static.prod-images.emergentagent.com/jobs/c7d28edb-cb45-4bc7-8f27-ae38510fc8f7/images/442bf51b4c226407282f8fe6e32d5d68422ac72a0c3420bf55c7d11b4050e2d4.png",
    portfolio: "https://static.prod-images.emergentagent.com/jobs/c7d28edb-cb45-4bc7-8f27-ae38510fc8f7/images/ec95e724e14cc2de1717a3da66b0b0ab5e4979cfd6e8dd93b60b338656ec5bea.png",
    clienteja: "https://static.prod-images.emergentagent.com/jobs/c7d28edb-cb45-4bc7-8f27-ae38510fc8f7/images/d3a19cdce24bd83199a0e37aa3980e23379e5af7ea1f450cd9c0c874c558bd97.png",
    postaja: "https://static.prod-images.emergentagent.com/jobs/c7d28edb-cb45-4bc7-8f27-ae38510fc8f7/images/12029c559fef1464cf90ca7a98b4ba6a0119dd0959e06f1a26671e567fbd57f1.png"
  };

  const products = [
    {
      id: 'orcamento',
      status: 'ready', // ready, coming
      badge: 'Pronto para ativação',
      icon: FileText,
      title: 'Orçamento na Hora',
      tagline: 'Seus clientes fazem orçamento sozinhos. Você não faz mais nada.',
      description: 'Sistema automatizado de orçamentos em PDF. Configure uma vez, compartilhe o link, receba pedidos 24h.',
      painPoints: [
        'Cansado de fazer orçamento manual?',
        'Perde tempo com clientes que só pedem preço?',
        'Quer parecer mais profissional?'
      ],
      benefits: [
        'Orçamentos ilimitados',
        'PDF com sua logo',
        'Link exclusivo personalizado',
        'Funciona 24h no automático',
        'Pagamento integrado'
      ],
      idealFor: ['Montadores', 'Marceneiros', 'Eletricistas', 'Pintores', 'Oficinas'],
      price: 'R$ 29,99/mês',
      setupNote: 'Teste grátis • Link ativado após assinatura',
      cta: 'Testar agora',
      link: '/orcamento',
      demoLink: '/orcamento/joao-montador',
      highlight: false,
      mockup: mockups.orcamento
    },
    {
      id: 'agenda',
      status: 'ready',
      badge: 'Pronto para ativação',
      icon: Calendar,
      title: 'Agenda Cheia',
      tagline: 'Acabou o cliente que marca e não aparece.',
      description: 'Sistema de agendamento online com lembretes automáticos no WhatsApp. Reduza faltas em até 80%.',
      painPoints: [
        'Clientes esquecem o horário?',
        'Perde dinheiro com "furos" na agenda?',
        'Cansado de ligar para confirmar?'
      ],
      benefits: [
        'Agendamento online 24h',
        'Lembrete automático WhatsApp',
        'Confirmação com 1 clique',
        'Reagendamento fácil'
      ],
      idealFor: ['Barbearias', 'Salões', 'Clínicas', 'Pet Shops', 'Consultórios'],
      price: 'R$ 49,99/mês',
      setupNote: 'Teste grátis • Link ativado após assinatura',
      cta: 'Testar agora',
      link: '/agenda',
      demoLink: '/agenda/barbearia-do-ze',
      highlight: false,
      mockup: mockups.agenda
    },
    {
      id: 'portfolio',
      status: 'ready',
      badge: 'Pronto para ativação',
      icon: Image,
      title: 'Meu Trampo',
      tagline: 'Mostre seu trabalho. Colete depoimentos. Venda mais.',
      description: 'Portfólio online profissional com coleta automática de depoimentos e avaliações de clientes.',
      painPoints: [
        'Fotos do trabalho perdidas no celular?',
        'Não sabe pedir avaliação ao cliente?',
        'Quer um portfólio profissional?'
      ],
      benefits: [
        'Página profissional bonita',
        'Galeria de projetos',
        'Depoimentos automáticos',
        'WhatsApp integrado'
      ],
      idealFor: ['Marceneiros', 'Arquitetos', 'Designers', 'Fotógrafos', 'Artesãos'],
      price: 'R$ 39,99/mês',
      setupNote: 'Teste grátis • Link ativado após assinatura',
      cta: 'Testar agora',
      link: '/meutrampo',
      demoLink: '/meutrampo/joao-reformas',
      highlight: false,
      mockup: mockups.portfolio
    },
    {
      id: 'clienteja',
      status: 'ready',
      badge: 'Pronto para ativação',
      icon: Users,
      title: 'ClienteJá',
      tagline: 'Controle clientes, parcelas e cobranças no automático.',
      description: 'Mini-CRM para lojistas. Cadastre clientes, venda parcelado e envie lembretes de cobrança pelo WhatsApp.',
      painPoints: [
        'Perde cliente por esquecer de cobrar?',
        'Parcelas anotadas em caderno?',
        'Cansado de mandar mensagem manual?'
      ],
      benefits: [
        'Cadastro rápido de clientes',
        'Parcelas automáticas',
        'Lembrete 1-click WhatsApp',
        'Campanhas de aniversário',
        'PIX/Boleto integrado'
      ],
      idealFor: ['Lojistas', 'Vendedores', 'MEIs', 'Autônomos', 'Prestadores'],
      price: 'A partir de R$ 49,99/mês',
      setupNote: 'Básico: 50 clientes • Crescimento: 200 • Pro: Ilimitado',
      cta: 'Testar agora',
      link: '/clienteja',
      demoLink: null,
      highlight: false,
      mockup: mockups.clienteja
    },
    {
      id: 'postaja',
      status: 'ready',
      badge: 'Novo! IA Generativa',
      icon: Sparkles,
      title: 'PostaJá',
      tagline: 'Conteúdo para redes sociais no automático. Feito por IA.',
      description: 'IA gera stories, posts e carrosséis personalizados para seu negócio todo dia. Você só baixa e publica.',
      painPoints: [
        'Não sabe o que postar?',
        'Sem tempo para criar conteúdo?',
        'Quer posts profissionais sem designer?'
      ],
      benefits: [
        '38+ conteúdos por mês',
        'Imagens + textos por IA',
        'Suas cores e estilo',
        'Stories + Posts + Carrosséis',
        'Notificação WhatsApp'
      ],
      idealFor: ['Lojistas', 'Influencers', 'Marcas', 'Autônomos', 'Empresas'],
      price: 'A partir de R$ 49,99/mês',
      setupNote: 'Básico: 38/mês • Plus: 48/mês com carrosséis',
      cta: 'Testar agora',
      link: '/postaja',
      demoLink: null,
      highlight: true,
      mockup: mockups.postaja
    }
  ];

  const handleCTA = (product) => {
    if (product.link) {
      navigate(product.link);
    } else {
      // Lista de espera - abre WhatsApp
      const message = `Olá! Tenho interesse no ${product.title} do AtivaJá. Quero entrar na lista de espera.`;
      window.open(`https://wa.me/5524993184232?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  return (
    <section id="solucoes" className="section">
      <div className="container">
        {/* Header da seção */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <Zap size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Soluções Prontas</span>
          </div>
          
          <h2 className="heading-2 mb-4">
            O que o AtivaJá já entrega hoje
          </h2>
          
          <p className="body-large">
            Sistemas <strong>prontos para usar</strong>. Sem projeto, sem espera.
            <br />
            Escolha, ative e comece a operar.
          </p>
        </div>

        {/* Cards de produtos - Grid responsivo melhorado */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16" data-testid="products-grid">
          {products.map((product) => (
            <div
              key={product.id}
              data-testid={`product-card-${product.id}`}
              className={`group relative bg-white rounded-2xl border overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
                product.highlight 
                  ? 'border-purple-300 shadow-lg ring-2 ring-purple-100' 
                  : 'border-slate-200 shadow-sm hover:border-blue-200'
              } ${product.status === 'coming' ? 'opacity-90' : ''}`}
            >
              {/* Highlight Glow Effect */}
              {product.highlight && (
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl opacity-20 blur group-hover:opacity-30 transition-opacity" />
              )}

              {/* Badge */}
              <div className={`absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-semibold ${
                product.highlight
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : product.status === 'ready' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-slate-100 text-slate-600'
              }`}>
                {product.badge}
              </div>

              {/* Product Mockup Image */}
              <div className="relative h-40 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
                <img
                  src={product.mockup}
                  alt={`${product.title} mockup`}
                  className="w-full h-full object-contain p-4 transform group-hover:scale-105 transition-transform duration-500"
                />
                {/* Overlay gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
              </div>

              {/* Content */}
              <div className="relative p-5">
                {/* Icon e título */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    product.highlight 
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                      : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                  }`}>
                    <product.icon size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 truncate">{product.title}</h3>
                    <p className="text-xs text-slate-500 font-medium line-clamp-2">{product.tagline}</p>
                  </div>
                </div>

                {/* Descrição curta */}
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{product.description}</p>

                {/* Benefícios principais (apenas 3) */}
                <div className="mb-4">
                  <ul className="space-y-1.5">
                    {product.benefits.slice(0, 3).map((benefit, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check size={14} className="text-green-500 flex-shrink-0" />
                        <span className="truncate">{benefit}</span>
                      </li>
                    ))}
                    {product.benefits.length > 3 && (
                      <li className="text-xs text-slate-400 pl-5">
                        +{product.benefits.length - 3} mais...
                      </li>
                    )}
                  </ul>
                </div>

                {/* Ideal para - compact */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1.5">
                    {product.idealFor.slice(0, 3).map((item, index) => (
                      <span 
                        key={index}
                        className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Preço */}
                <div className="mb-4 pb-4 border-b border-slate-100">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-xl font-bold ${product.highlight ? 'text-purple-600' : 'text-slate-900'}`}>
                      {product.price}
                    </span>
                  </div>
                  {product.setupNote && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></span>
                      {product.setupNote}
                    </p>
                  )}
                </div>

                {/* CTAs */}
                <div className="space-y-2">
                  <Button
                    onClick={() => handleCTA(product)}
                    data-testid={`product-cta-${product.id}`}
                    className={`w-full transition-all ${
                      product.highlight 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25' 
                        : product.status === 'ready' 
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white' 
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                  >
                    {product.cta}
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                  
                  {product.demoLink && (
                    <button
                      onClick={() => navigate(product.demoLink)}
                      className="w-full text-center text-xs text-blue-600 hover:text-blue-800 py-1.5 hover:underline"
                    >
                      Ver demonstração ao vivo →
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* AtivaJá Pro Banner - Premium Highlight */}
        <div className="mb-16 relative" data-testid="ativaja-pro-banner">
          {/* Animated glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-purple-500 to-pink-500 rounded-3xl blur-lg opacity-40 animate-pulse" />
          
          <div className="relative bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 rounded-2xl p-8 md:p-10 overflow-hidden border border-purple-500/30">
            {/* Background effects */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl" />
            
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              {/* Left content */}
              <div className="text-center lg:text-left flex-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full text-white text-sm font-bold mb-4 shadow-lg shadow-amber-500/30">
                  <Crown size={16} />
                  Melhor Custo-Benefício
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  AtivaJá <span className="bg-gradient-to-r from-amber-400 to-purple-400 bg-clip-text text-transparent">Pro</span>
                </h3>
                <p className="text-slate-300 text-lg mb-4 max-w-xl">
                  Todos os 5 produtos em uma única assinatura. 
                  A solução completa para automatizar seu negócio.
                </p>
                
                {/* Products included */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-4">
                  {['Orçamento', 'Agenda', 'Portfólio', 'ClienteJá', 'PostaJá'].map((prod, i) => (
                    <span key={i} className="px-3 py-1 bg-white/10 text-white/90 text-sm rounded-full border border-white/20">
                      <Check size={12} className="inline mr-1 text-green-400" />
                      {prod}
                    </span>
                  ))}
                </div>
                
                <p className="text-green-400 font-semibold text-lg">
                  💰 Economize R$ 79,96/mês comparado aos planos individuais
                </p>
              </div>

              {/* Right - Pricing */}
              <div className="flex flex-col items-center lg:items-end gap-4 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="text-center lg:text-right">
                  <p className="text-slate-400 text-sm line-through mb-1">De R$ 279,95/mês</p>
                  <div className="text-white">
                    <span className="text-5xl font-bold bg-gradient-to-r from-amber-400 to-purple-400 bg-clip-text text-transparent">R$ 199</span>
                    <span className="text-2xl text-white/80">,99</span>
                    <span className="text-slate-400 text-lg">/mês</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Cobrança mensal • Cancele quando quiser</p>
                </div>
                <Button 
                  onClick={() => navigate('/pro')}
                  data-testid="pro-banner-cta"
                  className="bg-gradient-to-r from-amber-500 to-purple-500 hover:from-amber-600 hover:to-purple-600 text-white font-bold px-8 py-3 text-lg shadow-lg shadow-purple-500/30 transition-all hover:scale-105"
                >
                  <Crown size={20} className="mr-2" />
                  Assinar Pro
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Diferencial */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-slate-900 rounded-2xl p-8 text-center">
            <Shield size={32} className="text-blue-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">
              Aqui não existe "em desenvolvimento"
            </h3>
            <p className="text-slate-400 text-sm">
              Todas as soluções marcadas como <span className="text-green-400 font-medium">"Pronto para ativação"</span> já 
              estão funcionando, testadas e podem ser usadas imediatamente.
              <br />
              <strong className="text-white">Sem improviso. Sem surpresas.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolucoesSection;
